import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Film, LogOut, Plus, LayoutDashboard, Building2, Sparkles, Crown, Zap, Menu, X, RotateCcw, Loader2, Shield, ArrowLeftRight, Gift } from "lucide-react";

const PlanBadge = ({ plan }) => {
  const p = (plan || "free").toLowerCase();
  const Icon = p === "agency" ? Crown : p === "pro" ? Zap : Sparkles;
  const cls =
    p === "agency"
      ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
      : p === "pro"
      ? "border-[#FF4400] text-[#FF4400] bg-[#FF4400]/10"
      : "border-zinc-800 text-zinc-500";
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 border rounded-sm font-mono text-[9px] uppercase tracking-widest font-bold ${cls}`} data-testid="navbar-plan-badge">
      <Icon className="w-3 h-3" />
      {p}
    </div>
  );
};

const Navbar = () => {
  const { user, logout, isImpersonating, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  const onAdminReset = async () => {
    if (resetting) return;
    if (!window.confirm("Reset del contatore mensile reel? I reel restano in libreria, vengono solo spostati al mese scorso.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-monthly-counter");
      toast.success(data.message || "Contatore resettato");
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore reset");
    } finally {
      setResetting(false);
    }
  };

  const onExitImpersonation = async () => {
    setExitingImpersonation(true);
    try {
      await endImpersonation();
      toast.success("Tornato come admin");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setExitingImpersonation(false);
    }
  };

  const navLinks = user ? [
    { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/brands", label: "Brand", Icon: Building2, testId: "nav-brands" },
    { to: "/workspace", label: "Workspace", Icon: Crown, testId: "nav-workspace" },
    { to: "/referrals", label: "Invita", Icon: Gift, testId: "nav-referrals" },
    { to: "/pricing", label: "Pricing", Icon: Sparkles, testId: "nav-pricing" },
    ...(user.is_admin ? [{ to: "/admin", label: "Admin", Icon: Shield, testId: "nav-admin" }] : []),
  ] : [];

  return (
    <>
      {isImpersonating && (
        <div className="bg-[#FFB800] text-black px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-[51]" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 min-w-0">
            <ArrowLeftRight className="w-4 h-4 flex-shrink-0" />
            <span className="font-mono text-[11px] uppercase tracking-widest font-bold truncate">
              Stai impersonificando · {user?.email}
            </span>
          </div>
          <button
            onClick={onExitImpersonation}
            disabled={exitingImpersonation}
            data-testid="exit-impersonation"
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-black text-[#FFB800] hover:bg-zinc-900 rounded-sm font-mono text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
          >
            {exitingImpersonation ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
            Esci
          </button>
        </div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-logo" className="flex items-center gap-2.5 group" onClick={closeMobile}>
          <img
            src="/reelio-icon-transparent.png"
            alt=""
            className="h-10 w-auto"
          />
          <span className="font-display font-black text-2xl tracking-tight text-white">
            Reelio
          </span>
        </Link>

        <nav className="flex items-center gap-1">
          {user ? (
            <>
              {navLinks.map(({ to, label, Icon, testId }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={testId}
                  className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white rounded-sm transition-colors"
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-body">{label}</span>
                </Link>
              ))}
              <button
                data-testid="nav-create-reel"
                onClick={() => { navigate("/create"); closeMobile(); }}
                className="inline-flex items-center gap-2 px-4 py-2 ml-1 text-sm font-bold bg-[#FF4400] text-white hover:bg-[#FF6633] rounded-sm transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Nuovo contenuto</span>
              </button>
              <div className="hidden md:flex items-center gap-3 pl-4 ml-2 border-l border-zinc-800">
                <div className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <PlanBadge plan={user.plan} />
                  </div>
                  <div className="text-sm text-white font-medium mt-0.5" data-testid="nav-user-name">{user.name}</div>
                </div>
                {user.is_admin && (
                  <button
                    onClick={onAdminReset}
                    disabled={resetting}
                    data-testid="nav-admin-reset"
                    title="Admin: reset contatore reel del mese"
                    className="w-9 h-9 rounded-sm flex items-center justify-center text-[#FFB800] hover:text-white hover:bg-[#FFB800]/20 border border-[#FFB800]/40 transition-colors disabled:opacity-50"
                  >
                    {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  </button>
                )}
                <button
                  data-testid="nav-logout"
                  onClick={() => { logout(); navigate("/"); }}
                  className="w-9 h-9 rounded-sm flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                  title="Esci"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
              {/* Mobile burger */}
              <button
                type="button"
                data-testid="nav-mobile-toggle"
                onClick={() => setMobileOpen((v) => !v)}
                className="md:hidden ml-1 w-9 h-9 rounded-sm flex items-center justify-center text-zinc-300 hover:text-white hover:bg-zinc-900 border border-zinc-800"
                aria-label={mobileOpen ? "Chiudi menù" : "Apri menù"}
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/pricing"
                data-testid="nav-pricing-public"
                className="hidden md:inline-flex items-center px-3 py-2 text-sm text-zinc-400 hover:text-white rounded-sm transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/login"
                data-testid="nav-login"
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white rounded-sm transition-colors"
              >
                Accedi
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold bg-[#FF4400] text-white hover:bg-[#FF6633] rounded-sm transition-colors"
              >
                Inizia Gratis
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* Mobile drawer */}
      {user && mobileOpen && (
        <div
          className="md:hidden absolute top-16 left-0 right-0 bg-[#0A0A0C] border-b border-zinc-800 shadow-2xl"
          data-testid="nav-mobile-drawer"
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-900">
            <div className="flex items-center gap-2">
              <PlanBadge plan={user.plan} />
              <span className="text-white text-sm font-medium">{user.name}</span>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); closeMobile(); }}
              data-testid="nav-mobile-logout"
              className="inline-flex items-center gap-1 text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest"
            >
              <LogOut className="w-3.5 h-3.5" />
              Esci
            </button>
          </div>
          <nav className="flex flex-col py-2">
            {navLinks.map(({ to, label, Icon, testId }) => (
              <Link
                key={to}
                to={to}
                data-testid={`${testId}-mobile`}
                onClick={closeMobile}
                className="flex items-center gap-3 px-5 py-3 text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
              >
                <Icon className="w-5 h-5 text-[#FF4400]" />
                <span className="font-body">{label}</span>
              </Link>
            ))}
            {user.is_admin && (
              <button
                type="button"
                onClick={() => { closeMobile(); onAdminReset(); }}
                data-testid="nav-admin-reset-mobile"
                disabled={resetting}
                className="flex items-center gap-3 px-5 py-3 text-[#FFB800] hover:text-white hover:bg-[#FFB800]/10 transition-colors text-left disabled:opacity-50"
              >
                {resetting ? <Loader2 className="w-5 h-5 animate-spin" /> : <RotateCcw className="w-5 h-5" />}
                <div className="flex flex-col">
                  <span className="font-body">Reset contatore reel</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB800]/60">Admin</span>
                </div>
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
    </>
  );
};

export default Navbar;
